import { motion, AnimatePresence } from 'framer-motion';
import { Home, MapPin, Link as LinkIcon, Users, Building } from 'lucide-react';
import { OrderFormData, PropertyType, Occupation } from '@/types/order';
import { Input } from '@/components/ui/input';

interface Step4BienProps {
  data: OrderFormData;
  onChange: (data: Partial<OrderFormData>) => void;
}

const typesBien: { value: PropertyType; label: string }[] = [
  { value: 'maison', label: 'Maison' },
  { value: 'appartement', label: 'Appartement' },
  { value: 'immeuble', label: 'Immeuble' },
  { value: 'terrain', label: 'Terrain' },
  { value: 'autre', label: 'Autre' },
];

const occupationOptions: { value: Occupation; label: string; description: string }[] = [
  { value: 'vide', label: 'Vide', description: 'Bien inoccupé' },
  { value: 'locataire', label: 'Locataire', description: 'Occupé par locataire' },
  { value: 'proprietaire', label: 'Propriétaire', description: 'Occupé par propriétaire' },
];

export function Step4Bien({ data, onChange }: Step4BienProps) {
  const showEtageFields = data.typeBien === 'appartement' || data.typeBien === 'immeuble';

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <div>
        <h3 className="text-3xl font-display font-bold text-navy-600 mb-2">
          Le bien immobilier
        </h3>
        <p className="text-lg text-navy-400">Informations sur le bien à visiter</p>
      </div>

      {/* Type de bien */}
      <div>
        <label htmlFor="typeBien" className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-3">
          <Home className="w-4 h-4 text-gold-600" />
          Type de bien *
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {typesBien.map((type) => (
            <button
              key={type.value}
              type="button"
              onClick={() => onChange({ typeBien: type.value })}
              className={`px-4 py-3 rounded-xl border-2 transition-all font-medium text-sm ${
                data.typeBien === type.value
                  ? 'border-gold-500 bg-gold-50 text-gold-700'
                  : 'border-navy-200 bg-white text-navy-600 hover:border-navy-300'
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>

      {/* Occupation */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-3">
          <Users className="w-4 h-4 text-gold-600" />
          Occupation du bien *
        </label>
        <div className="grid md:grid-cols-3 gap-4">
          {occupationOptions.map((option) => {
            const isSelected = data.occupation === option.value;
            return (
              <motion.button
                key={option.value}
                type="button"
                onClick={() => onChange({ occupation: option.value })}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                  isSelected
                    ? 'border-gold-500 bg-gold-50/50'
                    : 'border-navy-100 bg-white hover:border-navy-200'
                }`}
              >
                <p className={`font-semibold text-base mb-1 ${isSelected ? 'text-navy-600' : 'text-navy-600'}`}>
                  {option.label}
                </p>
                <p className="text-sm text-navy-400">{option.description}</p>
                {isSelected && (
                  <motion.div
                    layoutId="selectedOccupation"
                    className="absolute inset-0 rounded-xl border-2 border-gold-500 pointer-events-none"
                  />
                )}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Adresse */}
      <div>
        <label htmlFor="adresseBien" className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-2">
          <MapPin className="w-4 h-4 text-gold-600" />
          Adresse complète du bien *
        </label>
        <Input
          id="adresseBien"
          type="text"
          required
          value={data.adresseBien}
          onChange={(e) => onChange({ adresseBien: e.target.value })}
          placeholder="Rue de la Paix, 15, 1000 Bruxelles"
        />
        <p className="text-xs text-navy-400 mt-1">
          Adresse complète avec numéro, code postal et ville
        </p>
      </div>

      {/* Étage et ascenseur (conditionnel) */}
      <AnimatePresence>
        {showEtageFields && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="grid md:grid-cols-2 gap-4"
          >
            <div>
              <label htmlFor="etage" className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-2">
                <Building className="w-4 h-4 text-gold-600" />
                Étage
              </label>
              <Input
                id="etage"
                type="text"
                value={data.etage}
                onChange={(e) => onChange({ etage: e.target.value })}
                placeholder="Ex: 2ème étage, RDC..."
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-2">
                Ascenseur
              </label>
              <div className="flex items-center gap-4 h-[42px]">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="ascenseur"
                    checked={data.ascenseur === true}
                    onChange={() => onChange({ ascenseur: true })}
                    className="w-4 h-4 text-gold-500 border-navy-300 focus:ring-gold-500"
                  />
                  <span className="text-sm text-navy-600">Oui</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="ascenseur"
                    checked={data.ascenseur === false}
                    onChange={() => onChange({ ascenseur: false })}
                    className="w-4 h-4 text-gold-500 border-navy-300 focus:ring-gold-500"
                  />
                  <span className="text-sm text-navy-600">Non</span>
                </label>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lien annonce */}
      <div>
        <label htmlFor="lienAnnonce" className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-2">
          <LinkIcon className="w-4 h-4 text-gold-600" />
          Lien vers l'annonce (optionnel)
        </label>
        <Input
          id="lienAnnonce"
          type="url"
          value={data.lienAnnonce}
          onChange={(e) => onChange({ lienAnnonce: e.target.value })}
          placeholder="https://..."
        />
        <p className="text-xs text-navy-400 mt-1">
          Lien vers Biddit, Immoweb, ou autre plateforme
        </p>
      </div>

      {/* Particularités */}
      <div>
        <label htmlFor="particularites" className="block text-sm font-medium text-navy-600 mb-2">
          Particularités du bien (optionnel)
        </label>
        <p className="text-sm text-navy-400 mb-3">
          Informations importantes pour les visites : animaux, travaux, points d'attention...
        </p>
        <textarea
          id="particularites"
          rows={4}
          value={data.particularites}
          onChange={(e) => onChange({ particularites: e.target.value })}
          placeholder="Ex: Présence d'un chien, travaux de rénovation en cours, jardin avec portail fermé..."
          className="w-full px-4 py-3 rounded-xl border border-navy-200 focus:border-gold-500 focus:outline-none focus:ring-4 focus:ring-gold-500/20 transition-all resize-none text-navy-600 placeholder:text-navy-300"
        />
      </div>
    </motion.div>
  );
}
